// Mobil alt navigasyon barı — sadece giriş yapmış kullanıcıda ve sadece mobilde (md altı).
// Ortada vurgulu "Oluştur" FAB butonu; en sağda "Hesap" sekmesi alttan açılan hesap menüsünü açar.
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home, Clapperboard, Sparkles, Coins, User as UserIcon, LucideIcon,
  Receipt, Plug, BarChart3, LogOut, Shield, ChevronRight, Gift, Megaphone, TrendingUp, Scissors, LayoutTemplate,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn, fmtTR } from "@/lib/utils";

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
}

// Soldaki 2 sekme (orta FAB ve sağdaki Krediler + Hesap ayrı)
const leftItems: NavItem[] = [
  { label: "Ana Sayfa", icon: Home, path: "/" },
  { label: "Stüdyom", icon: Clapperboard, path: "/results" },
];

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, credits, plan, isAdmin, avatarUrl } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);

  // Admin panelinin kendi navigasyonu var → orada bottom nav gösterme.
  const show = !!user && !location.pathname.startsWith("/admin");

  // Bar görünürken sayfa içeriği bar'ın arkasında kalmasın diye body'ye alt boşluk sınıfı ekle.
  useEffect(() => {
    if (!show) return;
    document.body.classList.add("has-bottom-nav");
    return () => document.body.classList.remove("has-bottom-nav");
  }, [show]);

  if (!show) return null;

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const go = (path: string) => {
    setAccountOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setAccountOpen(false);
    await supabase.auth.signOut();
    navigate("/");
    window.location.reload();
  };

  const NavButton = ({ item }: { item: NavItem }) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    return (
      <button
        onClick={() => {
          // Zaten o sayfadaysan en üste kaydır; değilsen sayfaya git.
          if (location.pathname === item.path) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            navigate(item.path);
          }
        }}
        aria-label={item.label}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[0.65rem] font-medium transition-colors",
          active ? "text-primary" : "text-muted-foreground hover:text-foreground",
        )}
      >
        <Icon className={cn("h-5 w-5 transition-transform", active && "scale-110")} />
        <span className="leading-none">{item.label}</span>
      </button>
    );
  };

  const accountLinks = [
    { label: "Stüdyom", icon: Clapperboard, path: "/results" },
    { label: "🔗 Bağlantıdan Reklam", icon: Megaphone, path: "/results?tool=url" },
    { label: "🖼️ Ürün Görsel Seti", icon: LayoutTemplate, path: "/results?tool=set" },
    { label: "🧹 Arkaplan Temizleyici", icon: Scissors, path: "/results?tool=arkaplan" },
    { label: "⬆️ Kalite Yükseltici", icon: LayoutTemplate, path: "/results?tool=yukselt" },
    { label: "👗 Sanal Deneme", icon: LayoutTemplate, path: "/results?tool=deneme" },
    { label: "🕺 Hareket Taklidi", icon: LayoutTemplate, path: "/results?tool=hareket" },
    { label: "🎤 Video Dublaj", icon: Megaphone, path: "/results?tool=dublaj" },
    { label: "🎭 Yüz Değiştirme", icon: LayoutTemplate, path: "/results?tool=yuz" },
    { label: "🧩 Parçalara Ayırma", icon: LayoutTemplate, path: "/results?tool=parcala" },
    { label: "🧬 Video Klonlama", icon: LayoutTemplate, path: "/results?tool=klon" },
    { label: "🪧 Kampanya Afişi", icon: Megaphone, path: "/results?tool=afis" },
    { label: "🎥 Referanstan Üretim", icon: Clapperboard, path: "/results?tool=referans" },
    { label: "🚀 Kampanya Motoru", icon: Megaphone, path: "/kampanya" },
    { label: "🔥 Trend Avcısı", icon: TrendingUp, path: "/trend" },
    { label: "✂️ Videoyu Kliple", icon: Scissors, path: "/repurpose" },
    { label: "💼 Şablon Marketplace", icon: LayoutTemplate, path: "/sablonlar" },
    { label: "🧑‍🎤 Influencer'larım", icon: UserIcon, path: "/influencers" },
    { label: "🎁 Davet Et & Kazan", icon: Gift, path: "/davet" },
    { label: "Ödeme Geçmişi", icon: Receipt, path: "/payment-history" },
    { label: "Entegrasyonlar", icon: Plug, path: "/integrations" },
    { label: "Video Analizleri", icon: BarChart3, path: "/analytics" },
  ];

  return (
    <>
      <nav
        aria-label="Mobil alt menü"
        className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-lg pb-[env(safe-area-inset-bottom)]"
      >
        <div className="relative flex items-stretch">
          {leftItems.map((item) => (
            <NavButton key={item.path} item={item} />
          ))}

          {/* Ortadaki vurgulu FAB — Oluştur (video oluşturma /results'ta) */}
          <div className="flex flex-1 items-start justify-center">
            <button
              onClick={() => navigate("/results")}
              aria-label="Yeni video oluştur"
              className="-mt-5 flex h-14 w-14 flex-col items-center justify-center gap-0.5 rounded-full bg-gradient-primary text-white shadow-lg shadow-primary/30 ring-4 ring-background transition-transform active:scale-95"
            >
              <Sparkles className="h-5 w-5" />
              <span className="text-[0.55rem] font-semibold leading-none">Oluştur</span>
            </button>
          </div>

          <NavButton item={{ label: "Krediler", icon: Coins, path: "/pricing" }} />

          {/* Hesap — alttan açılan hesap menüsünü açar */}
          <button
            onClick={() => setAccountOpen(true)}
            aria-label="Hesap"
            className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[0.65rem] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <UserIcon className="h-5 w-5" />
            <span className="leading-none">Hesap</span>
          </button>
        </div>
      </nav>

      {/* Hesap menüsü */}
      <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
        {/* max-h + overflow ŞART: 20+ link ekranı aşıyor; bunlar olmadan menü kaydırılamıyor (mobil taşma bug'ı) */}
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)] max-h-[85dvh] overflow-y-auto overscroll-contain">
          <SheetHeader className="text-left">
            <SheetTitle>Hesabım</SheetTitle>
          </SheetHeader>

          {/* Kullanıcı özeti */}
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3">
            <Avatar className="h-11 w-11">
              <AvatarImage src={avatarUrl} alt={user?.email || ""} />
              <AvatarFallback className="bg-gradient-primary text-white">
                {user?.email?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.email}</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">{plan}</span>
                <span className="flex items-center gap-1">
                  <Coins className="h-3 w-3" />
                  {fmtTR(credits)} kredi
                </span>
              </div>
            </div>
            <button
              onClick={() => go("/pricing")}
              className="shrink-0 rounded-lg bg-gradient-primary px-3 py-1.5 text-xs font-semibold text-white"
            >
              Kredi Al
            </button>
          </div>

          {/* Hesap linkleri */}
          <div className="mt-3 space-y-1">
            {accountLinks.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => go(link.path)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-accent transition-colors"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-left">{link.label}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              );
            })}

            {isAdmin && (
              <button
                onClick={() => go("/admin")}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-primary hover:bg-accent transition-colors"
              >
                <Shield className="h-4 w-4" />
                <span className="flex-1 text-left font-medium">Admin Paneli</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="flex-1 text-left">Çıkış Yap</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
